import {createStore} from '../utilities/storeUtils';
import  _ from 'underscore';
import AsnDetailStore from './AsnDetailStore';
import {asnKeyNormalizr} from './relationships';
import {isTruthy, getHoliday, getWeekend, convertMilitaryToInt, isPresent} from "../utilities/general";
import QuoteStore from "./QuoteStore";


const PLANNED = "Planned";

let general = {};
let asns = {};
const defaults = {
  // NOTE: for now, just hard code the only facility
  // because we only have one
  facilities: "1",
  pickupTimeFrom: "09:00",
  pickupTimeTo: "17:00",
  status: "New"
};

const AsnStore = createStore({
  storeEventBaseKey: "asns",
  normalizr: asnKeyNormalizr,
  normalizrKey: "asns",
  getData(){
    return asns;
  },
  getDefaults(){
    return defaults;
  },
  requestQuoteSucceeded(asn){
    return _.has(asn, "requestQuoteSuccess") && asn.requestQuoteSuccess;
  },
  requestQuoteFailed(asn){
    // does not explicitly equal false
    return _.has(asn, "requestQuoteSuccess") && asn.requestQuoteSuccess === false;
  },
  hasSpecialQuote(asn){
    return _.has(asn, "specialQuote") && asn.specialQuote;
  },
  didRequestQuotes(asn){
    return this.hasSpecialQuote(asn) || this.requestQuoteSucceeded(asn) || this.requestQuoteFailed(asn);
  },
  hasSelectedQuote(asn){
    return !!this.getSelectedQuote(asn);
  },
  getSelectedQuote(asn){
    return this.getSelectedQuoteForAsn(asn);
  },
  getSelectedQuoteForAsn(asn){
    const attrs = _(asn).pick("quoteId", "optionId");
    return QuoteStore.findWhere(attrs);
  },
  getAvailableQuotesForAsn(asn){
    return QuoteStore.where({asnId: asn.id});
  },
  getAvailableAndSelectedQuotesForAsn(asn){
    return QuoteStore.filter(filterQuoteAvailableOrSelectedForAsn, asn);
  },
  resetQuotesRelatedFlags(asn, overrides={}){
    if(asn){
      const quotes = this.getAvailableAndSelectedQuotesForAsn(asn);
      QuoteStore.removeDataByIdsAndEmit(QuoteStore.pluckIdsFromArray(quotes));
      // create a new snapshot based on most
      const newData = _.extend({},  {quoteId: null, optionId: null, quotesAreValid: null, showQuotesRemovedBecauseInfoChangedMessage: null, isFetchingQuotes: null, requestQuoteSuccess: null, specialQuote: null}, overrides);
      this.updateObjectAndEmit(asn.id, newData);
    }
  },
  areQuotesInvalid(asn){
    return _.has(asn, "quotesAreValid") && asn.quotesAreValid === false;
  },
  shouldShowQuotesRemovedBecauseInfoChangedMessage(asn){
    return !!asn.showQuotesRemovedBecauseInfoChangedMessage;
  },

  isFetchingQuotes(asn){
    return asn.isFetchingQuotes;
  },
  isSubmitted(asn){
    const status = asn.status;
    return status && status !== PLANNED;
  },
  isPallet(asn){
    return asn.shippingMethod === "pallet";
  },
  isParcel(asn){
    return asn.shippingMethod === "parcel";
  },
  getCarrierBroker(){
    return general.carrierBrokerSelection;
  },
  selectCarrierBroker(value){
    this.selectCarrierBrokerMethod(value);
  },
  isGTL(asn){
    return this.isPallet(asn) && this.hasShippingPartner(asn);
  },
  selectParcel(){
    this.selectShippingMethod('parcel');
  },
  selectPallet(){
    this.selectShippingMethod('pallet');
  },
  selectShippingMethod(method){
    general.shippingMethod = method;
    this.emitEvent('shipping_method_changed', method);
  },
  selectCarrierBrokerMethod(method){
    general.carrierBrokerSelection = method;
    this.emitEvent('carrier_broker_method_changed', method);
  },

  hasShippingPartner(asn){
    return isTruthy(asn.shippingPartner);
  },
  hasShippingPartnerSelected(asn){
    return !_.isNull(asn.shippingPartner) && !_.isUndefined(asn.shippingPartner);
  },

  selectShippingPartner(asn, status){
    status = isTruthy(status);
    asn.shippingPartner = status;
    this.emitEvent('shipping_partner_changed', status);
  },
  selectFreightShipping(asn, status){
    status = isTruthy(status);
    asn.freightShipping = status;
    this.emitEvent('freight_shipping_changed', status);
  },
  hasFreigthSelected(asnId){
    const asn = this.getAsn(asnId);
    if(asn && isPresent(asn.freightShipping)){
      return isTruthy(asn.freightShipping);
    }else{
      return undefined;
    }
  },
  selectCaseShipping(asn, status){
    status = isTruthy(status);
    asn.caseShipping = status;
    this.emitEvent('case_shipping_changed', status);
  },
  hasCaseShippingSelected(asnId){
    return isTruthy(this.getAsn(asnId).caseShipping);
  },
  createNewAsn(data){
    data = _.extend({id: _.uuid()}, defaults, data);
    return this.addAsn(data);
  },
  addAsn(data){
    return this.addObjectsAndEmit(asns, data);
  },
  getAsnWithShippingMethod(shippingMethod){
    if(_.isUndefined(shippingMethod)){
      shippingMethod = this.getShippingMethod();
    }
    return _.chain(asns).values().find((asn)=>{
      return asn.shippingMethod === shippingMethod;
    }).value();
  },
  getShippingMethod(){
    return general.shippingMethod;
  },

  getOrCreateAsnWithShippingMethod(shippingMethod){
    let asn =  _(asns).find((asn)=>{
      return asn.shippingMethod === shippingMethod;
    });
    if(!asn){
      asn = this.createNewAsn({shippingMethod});
    }
    return asn;
  },
  allAsnDetailsHaveDimensionsAndWeight(asn){
    const asnDetails = this.getAsnDetails(asn);
    return AsnDetailStore.haveDimensionsAndWeight(asnDetails);
  },
  allAsnShippingPalletsHaveDimensionsAndWeight(asn){
    const asnShippingPallets = this.getAsnShipppingPallets(asn);
    return AsnShippingPalletStore.haveDimensionsAndWeight(asnShippingPallets);
  },
  hasAllPickupTimeFields(asn){
    return (
      isPresent(asn.pickupTimeFrom) &&
      isPresent(asn.pickupTimeTo)
    );
  },
  getPickupDateHoliday(asn){
    return getHoliday(asn.pickupDate, {holidayLookup: "shippingHolidays"});
  },
  getPickupDateWeekend(asn){
    return getWeekend(asn.pickupDate);
  },
  getAsnDetails(asn){
    return AsnDetailStore.where({asnId: asn.id});
  },

  getAsnShipppingPallets(asn){
   return AsnShippingPalletStore.where({asnId: asn.id});
  },
  isPickupTimeRangeValid(asn){
    return convertMilitaryToInt(asn.pickupTimeFrom) < convertMilitaryToInt(asn.pickupTimeTo);
  },

  getAsns(){
    return asns;
  },
  getAsn(id){
    return asns[id];
  },


  getAllGeneralData(){
    return general;
  }
})


function filterQuoteAvailableOrSelectedForAsn(quote){
  const asn = this;
  return (quote.asnId === asn.id || (asn.quoteId === quote.quoteId && asn.optionId === quote.optionId))&&quote.estimatedCost!='';
}

window.AsnStore = AsnStore;
export default AsnStore



// WEBPACK FOOTER //
// ./src/stores/AsnStore.js